import React, { useState } from 'react';
import './Contact.css';
import { FaEnvelope, FaMapMarkerAlt, FaLinkedin, FaGithub, FaKaggle, FaPaperPlane, FaSpinner, FaCheckCircle } from 'react-icons/fa';

const CONTACT_EMAIL = 'sanjaysakthi2311@gmail.com';
const FORMSUBMIT_ENDPOINT = `https://formsubmit.co/ajax/${CONTACT_EMAIL}`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      _subject: `Portfolio Contact: ${formData.subject.trim()}`,
      _captcha: 'false'
    };

    try {
      const response = await fetch(FORMSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('FormSubmit request failed');
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      const mailtoSubject = encodeURIComponent(formData.subject.trim());
      const mailtoBody = encodeURIComponent(
        `Hi Sanjay,\n\nName: ${formData.name.trim()}\nEmail: ${formData.email.trim()}\n\n${formData.message.trim()}\n`
      );

      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${mailtoSubject}&body=${mailtoBody}`;
      setSubmitStatus('fallback');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 4000);
    }
  };

  const contactInfo = [
    { icon: <FaEnvelope />, title: 'Email', value: CONTACT_EMAIL, link: `mailto:${CONTACT_EMAIL}` },
    { icon: <FaMapMarkerAlt />, title: 'Location', value: 'Dindigul, Tamil Nadu', link: null },
    { icon: <FaLinkedin />, title: 'LinkedIn', value: 'linkedin.com/in/-sanjay/', link: 'https://linkedin.com/in/-sanjay/' }
  ];

  const socialLinks = [
    { name: 'GitHub', icon: <FaGithub />, url: 'https://github.com/sanjay2320', color: '#9ca3af' },
    { name: 'LinkedIn', icon: <FaLinkedin />, url: 'https://linkedin.com/in/-sanjay/', color: '#38bdf8' },
    { name: 'Kaggle', icon: <FaKaggle />, url: 'https://www.kaggle.com/sanjay2311', color: '#22d3ee' }
  ];

  const quickStats = [
    { label: 'Response', value: '< 24 hrs' },
    { label: 'Focus', value: 'Analytics + ML' },
    { label: 'Timezone', value: 'IST' }
  ];

  return (
    <section id="contact" className="contact section">
      <div className="container">
        <h2 className="section-title">Get In Touch</h2>
        <p className="section-subtitle">
          Have a project in mind or just want to connect? I&apos;d like to hear from you.
        </p>

        <div className="contact-content">
          <div className="contact-panel contact-overview">
            <span className="contact-kicker">Contact</span>
            <div className="contact-intro">
              <h3>Let&apos;s build something useful.</h3>
              <p>
                I work on analytics, dashboards, and ML systems that help teams make better decisions.
                If that sounds relevant, reach out.
              </p>
            </div>

            <div className="contact-stats">
              {quickStats.map((stat) => (
                <div key={stat.label} className="contact-stat">
                  <span className="contact-stat-value">{stat.value}</span>
                  <span className="contact-stat-label">{stat.label}</span>
                </div>
              ))}
            </div>

            <div className="contact-details">
              {contactInfo.map((info, index) => {
                const isLink = Boolean(info.link);

                if (!isLink) {
                  return (
                    <div key={index} className="contact-item static-contact-item">
                      <div className="contact-icon">{info.icon}</div>
                      <div className="contact-detail">
                        <h4>{info.title}</h4>
                        <span>{info.value}</span>
                      </div>
                    </div>
                  );
                }

                return (
                  <a key={index} className="contact-item" href={info.link} target="_blank" rel="noopener noreferrer">
                    <div className="contact-icon">{info.icon}</div>
                    <div className="contact-detail">
                      <h4>{info.title}</h4>
                      <span>{info.value}</span>
                    </div>
                  </a>
                );
              })}
            </div>

            <div className="social-links">
              <h4>Elsewhere</h4>
              <div className="social-icons">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    style={{ '--social-color': social.color }}
                    title={social.name}
                  >
                    {social.icon}
                    <span>{social.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="contact-panel contact-form-container">
            <div className="form-copy">
              <span className="form-kicker">Start a conversation</span>
              <h3>Share your project brief</h3>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="Your full name" />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your.email@example.com" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required placeholder="Dashboard, analytics support, ML workflow..." />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows="6" placeholder="Tell me about the problem, goal, or idea you want to work on."></textarea>
              </div>

              <button type="submit" className={`btn submit-btn ${isSubmitting ? 'submitting' : ''}`} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <FaSpinner className="fa-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FaPaperPlane />
                    Send Message
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="submit-success">
                  <FaCheckCircle />
                  Message sent successfully. I&apos;ll get back to you soon.
                </div>
              )}

              {submitStatus === 'fallback' && (
                <div className="submit-error">
                  Couldn&apos;t submit online. Your email app was opened as backup.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
